/**
 * 用户偏好客户端 (B1 + B2 类) —— 阶段 A 接入 `/api/me/prefs` 持久化。
 *
 * 之前 SPA 的偏好散落在多个 zustand persist 中间件 + 直接的 localStorage.{getItem,setItem}
 * 调用里，跨电脑 / 跨浏览器登录就丢。阶段 A 把所有「跟账号走」的偏好搬到服务器，
 * SPA 启动时一次性拉满 GET /api/me/prefs，PUT 防抖回写。
 *
 * 类型与 `src/server/userPrefs.ts` 对齐；schema 调整时两端一起改。
 */
import { create } from 'zustand';
import { buildUrl } from './api';
import { API_CREDENTIALS } from './apiBase';

/* ----------------------------------------------------------------- */
/* Schema (mirror of src/server/userPrefs.ts)                        */
/* ----------------------------------------------------------------- */

export interface UiPrefs {
  theme: 'dark' | 'light' | 'auto';
  displayName: string;
  lastSerial: string | null;
  dashboard: { collapsed: Record<string, boolean> };
  sidebar: { collapsed: boolean };
}

export interface MirrorPrefs {
  /** scrcpy --max-size 档位。null = 使用 server transport 默认；-1 = 自动；0 = 原生分辨率。 */
  maxSize: number | null;
  /** scrcpy --max-fps。null = 默认；-1 = 自动。 */
  maxFps: number | null;
  /** scrcpy 视频比特率（bps）。null = 使用服务端 transport 默认。 */
  bitRate: number | null;
  /** 浮动镜像窗口位置 + 尺寸。null = 使用 SPA 默认（贴右下角）。 */
  layout: { width: number; height: number; x: number; y: number } | null;
  /** 镜像窗口下方折叠的 logcat 面板。 */
  showLogcat: boolean;
  /** scrcpy server 内置日志面板。 */
  showServerLog: boolean;
  /** 顶部 FPS / bitrate / latency HUD 显示。 */
  hudVisible: boolean;
  /** 设备保持唤醒（scrcpy stay_awake）。 */
  stayAwake: boolean;
  /** 锁定视频朝向（-1 unlocked / 0/1/2/3 = 各方向）。 */
  lockOrientation: number;
  /** Document-PiP fallback：把窗口提到全局顶层。 */
  pinned: boolean;
  /** 右侧工具栏宽度 (CSS px)。 */
  sideToolsWidth: number;
  /** 工具栏分组折叠状态 (groupId → collapsed)。仅记 true 的分组。 */
  sideToolsCollapsed: Record<string, boolean>;
}

export interface TransportPrefs {
  preferred: 'usb' | 'wifi' | 'auto';
}

export interface AutoswitchPrefs {
  enabled: boolean;
}

export interface ProxyPrefs {
  mru: string[];
}

export interface NetworkBookmark {
  host: string;
  port: number;
  label: string;
}

export interface NetworkPrefs {
  bookmarks: NetworkBookmark[];
}

export interface TerminalPrefs {
  font: string;
  fontSize: number;
  shell: string;
}

export interface SessionsPrefs {
  recentSerials: string[];
}

export interface CommandsPrefs {
  pinned: string[];
  recent: string[];
}

/**
 * @deprecated The helper-restart detector now stores its baseline in
 * `localStorage` (key `adh.lastSeenLoadedAt.v1`). The prefs path was
 * abandoned because `updateGlobal` debounces the PUT by 300ms while
 * `resetServiceWorkerAndCaches() + location.reload()` finishes in tens
 * of ms; the reload would beat the PUT, the next boot would see the
 * stale baseline, and the page would loop through several reloads.
 *
 * The schema is preserved so older SPA bundles served from a stale
 * precache can still parse the `/api/me/prefs` response, and so the
 * server-side userPrefs schema doesn't need a coordinated bump.
 */
export interface RuntimeConfigPrefs {
  lastSeenLoadedAt: number | null;
}

export interface GlobalPrefs {
  ui: UiPrefs;
  mirror: MirrorPrefs;
  transport: TransportPrefs;
  autoswitch: AutoswitchPrefs;
  proxy: ProxyPrefs;
  network: NetworkPrefs;
  terminal: TerminalPrefs;
  sessions: SessionsPrefs;
  commands: CommandsPrefs;
  runtimeConfig: RuntimeConfigPrefs;
  hubSsid: string | null;
}

/**
 * Clipboard history per device — see src/server/userPrefs.ts for the
 * authoritative shape. Server stores it transparently (Record<string,
 * unknown>); SPA owns the LRU / dedup / pinned semantics in
 * web/src/lib/clipboard/store.ts.
 */
export interface ClipboardEntry {
  id: string;
  text: string;
  source: 'host' | 'device' | 'manual';
  pinned?: boolean;
  createdAt: number;
}

export interface ClipboardDevicePrefs {
  entries: ClipboardEntry[];
  lastSeenAt: number | null;
}

export interface DevicePrefs {
  log: Record<string, unknown>;
  logWatch: { keys: string[] };
  checkpoint: Record<string, unknown>;
  inspector: Record<string, unknown>;
  selectedPkg: string | null;
  packages: Record<string, Record<string, unknown>>;
  clipboard: ClipboardDevicePrefs;
}

export type DevicesPrefs = Record<string, DevicePrefs>;

/* ----------------------------------------------------------------- */
/* Defaults — used until first fetch resolves                        */
/* ----------------------------------------------------------------- */

/**
 * 跟 [web/index.html](../../index.html) 的 boot script 共用
 * `adh.theme.v1` 缓存键。必须读 cache 而非硬编码 `'auto'`：
 *
 * React 第一帧 Topbar 会基于 `store.ui.theme` 决定要不要 `add/remove`
 * `html.dark` class；如果 default 给 `'auto'` 而 boot script 已经按
 * cache 加了 `dark`，那 Topbar 第一次跑 useEffect 会立刻
 * `removeClass('dark')` 制造一次 FOUC（用户切了暗色 → 刷新 → 闪一下浅
 * 色 → 等 myPrefs.refresh 拉回 dark 才恢复；如果 adh 离线 refresh 永远
 * 不回填，主题就永久卡在浅色 = 用户报告的 bug）。
 *
 * 把 default 跟 boot 对齐后，第一帧 className 行为已经正确，
 * refresh() 拉到 B1 真值再 reconcile（通常是 no-op）。
 *
 * 项目规则 §2 通常禁止新增 localStorage 偏好；`adh.theme.v1` 属于
 * `adh.runtimeConfig.v1` 同族的"运行时缓存"显式豁免（B1 仍是真值，这
 * 里只是 FOUC 防抖用的镜像）。
 */
function readBootThemeCache(): 'auto' | 'dark' | 'light' {
  if (typeof window === 'undefined' || !window.localStorage) return 'auto';
  try {
    const t = window.localStorage.getItem('adh.theme.v1');
    if (t === 'dark' || t === 'light') return t;
  } catch {
    /* private mode / quota — 沿用 'auto'，由 boot script 的
       prefers-color-scheme fallback 兜底。 */
  }
  return 'auto';
}

const defaultGlobal: GlobalPrefs = {
  ui: {
    theme: readBootThemeCache(),
    displayName: '',
    lastSerial: null,
    dashboard: { collapsed: {} },
    sidebar: { collapsed: false },
  },
  mirror: {
    maxSize: null,
    maxFps: null,
    bitRate: null,
    layout: null,
    showLogcat: false,
    showServerLog: false,
    hudVisible: true,
    stayAwake: false,
    lockOrientation: -1,
    pinned: false,
    sideToolsWidth: 48,
    sideToolsCollapsed: {},
  },
  transport: { preferred: 'auto' },
  autoswitch: { enabled: false },
  proxy: { mru: [] },
  network: { bookmarks: [] },
  terminal: { font: '', fontSize: 13, shell: '' },
  sessions: { recentSerials: [] },
  commands: { pinned: [], recent: [] },
  runtimeConfig: { lastSeenLoadedAt: null },
  hubSsid: null,
};

const defaultDevice: DevicePrefs = {
  log: {},
  logWatch: { keys: [] },
  checkpoint: {},
  inspector: {},
  selectedPkg: null,
  packages: {},
  clipboard: { entries: [], lastSeenAt: null },
};

/* ----------------------------------------------------------------- */
/* Store                                                             */
/* ----------------------------------------------------------------- */

type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;

export interface MyPrefsState {
  global: GlobalPrefs;
  devices: DevicesPrefs;
  /** True after the initial GET /api/me/prefs resolves. */
  ready: boolean;
  /** True while a PUT is in flight (any). Used by Settings page status badge. */
  saving: boolean;
  /** Last error from a PUT, if any. Cleared on success. */
  lastError: string | null;

  /** Pull the full bundle. Idempotent — safe to call multiple times. */
  init(): Promise<void>;
  /** Force a fresh GET (bypasses ready check). */
  refresh(): Promise<void>;

  /**
   * Optimistic update — applies `patch` locally immediately and schedules a
   * debounced PUT. Multiple rapid writes coalesce into a single request
   * with the deep-merged payload. Use this for typing-rate updates
   * (theme picker, sliders, etc.).
   */
  updateGlobal(patch: DeepPartial<GlobalPrefs>): void;
  updateDevice(serial: string, patch: DeepPartial<DevicePrefs>): void;

  /**
   * Force-flush any pending debounced PUTs. Call from `beforeunload` (the
   * SPA wires this in once at boot) so a tab close doesn't drop a pending
   * write on the floor.
   */
  flush(): Promise<void>;
}

const DEBOUNCE_MS = 300;

interface PendingState {
  global: DeepPartial<GlobalPrefs> | null;
  devices: Record<string, DeepPartial<DevicePrefs>>;
  timer: ReturnType<typeof setTimeout> | null;
  inflight: Promise<void> | null;
}

const pending: PendingState = {
  global: null,
  devices: {},
  timer: null,
  inflight: null,
};

export const useMyPrefsStore = create<MyPrefsState>((set, get) => ({
  global: defaultGlobal,
  devices: {},
  ready: false,
  saving: false,
  lastError: null,

  async init() {
    if (get().ready) return;
    await get().refresh();
  },

  async refresh() {
    try {
      const res = await fetch(buildUrl('/api/me/prefs'), { credentials: API_CREDENTIALS });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Stage B.3 — `agent` field removed from /api/me/prefs.
      // SPA fetches /api/agent/config separately when it needs it
      // (Settings → Agent tab; agentConfig.ts handles that).
      const body = (await res.json()) as { global: GlobalPrefs; devices: DevicesPrefs };
      const merged = deepMergeKnown(defaultGlobal, body.global);
      set({
        global: merged,
        devices: coerceDevices(body.devices),
        ready: true,
        lastError: null,
      });
      // Sync the authoritative B1 ui.theme to the index.html pre-React
      // boot cache (`adh.theme.v1`). Without this, a user who set
      // `dark` on browser A would still see light flash on browser B
      // the first time it loads — Topbar's useEffect updates the cache
      // only AFTER React mounts and re-renders with the synced prefs.
      // We only mirror explicit dark/light; 'auto' leaves the cache
      // untouched so the boot script's prefers-color-scheme fallback
      // takes over.
      const t = merged.ui.theme;
      if (t === 'dark' || t === 'light') {
        try {
          localStorage.setItem('adh.theme.v1', t);
        } catch {
          /* private mode / quota — accept the FOUC on next reload */
        }
      }
    } catch (e) {
      // Defaults already in store. Mark ready so UI doesn't hang on a flaky fetch
      // — user changes will retry-on-PUT and surface lastError there.
      set({ ready: true, lastError: (e as Error).message });
    }
  },

  updateGlobal(patch) {
    set((s) => ({ global: deepMerge(s.global, patch) }));
    pending.global = pending.global ? deepMerge(pending.global, patch) : patch;
    schedule(set, get);
  },

  updateDevice(serial, patch) {
    set((s) => ({
      devices: {
        ...s.devices,
        [serial]: deepMerge(s.devices[serial] ?? defaultDevice, patch),
      },
    }));
    pending.devices[serial] = pending.devices[serial]
      ? deepMerge(pending.devices[serial], patch)
      : patch;
    schedule(set, get);
  },

  async flush() {
    if (pending.timer) {
      clearTimeout(pending.timer);
      pending.timer = null;
    }
    if (pending.inflight) await pending.inflight;
    if (!pending.global && Object.keys(pending.devices).length === 0) return;
    await drain(set, get);
  },
}));

function schedule(
  set: (partial: Partial<MyPrefsState>) => void,
  get: () => MyPrefsState,
): void {
  if (pending.timer) clearTimeout(pending.timer);
  pending.timer = setTimeout(() => {
    void drain(set, get);
  }, DEBOUNCE_MS);
}

async function drain(
  set: (partial: Partial<MyPrefsState>) => void,
  _get: () => MyPrefsState,
): Promise<void> {
  if (pending.inflight) {
    // Coalesce next batch behind the in-flight one.
    await pending.inflight;
  }
  const globalPatch = pending.global;
  const devicePatches = pending.devices;
  pending.global = null;
  pending.devices = {};
  pending.timer = null;
  if (!globalPatch && Object.keys(devicePatches).length === 0) return;

  set({ saving: true });
  pending.inflight = (async () => {
    try {
      if (globalPatch) {
        const res = await fetch(buildUrl('/api/me/prefs/global'), {
          method: 'PUT',
          credentials: API_CREDENTIALS,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(globalPatch),
        });
        if (!res.ok) throw new Error(`global PUT failed: HTTP ${res.status}`);
      }
      for (const [serial, patch] of Object.entries(devicePatches)) {
        const res = await fetch(buildUrl(`/api/me/prefs/devices/${encodeURIComponent(serial)}`), {
          method: 'PUT',
          credentials: API_CREDENTIALS,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        });
        if (!res.ok) throw new Error(`device[${serial}] PUT failed: HTTP ${res.status}`);
      }
      set({ saving: false, lastError: null });
    } catch (e) {
      set({ saving: false, lastError: (e as Error).message });
    } finally {
      pending.inflight = null;
    }
  })();
  await pending.inflight;
}

/* ----------------------------------------------------------------- */
/* Hooks                                                             */
/* ----------------------------------------------------------------- */

/**
 * Read-and-write hook for the B1 (跟账号走) bundle. Returns the current
 * snapshot plus a setter that takes a DeepPartial patch. Components subscribe
 * to a slice via `selector` to avoid re-rendering on unrelated changes.
 *
 * Example:
 *   const theme = useGlobalPrefs((g) => g.ui.theme);
 *   const update = useGlobalPrefsUpdate();
 *   update({ ui: { theme: 'dark' } });
 */
export function useGlobalPrefs<T = GlobalPrefs>(selector?: (g: GlobalPrefs) => T): T {
  return useMyPrefsStore((s) => (selector ? selector(s.global) : (s.global as unknown as T)));
}

export function useGlobalPrefsUpdate(): (patch: DeepPartial<GlobalPrefs>) => void {
  return useMyPrefsStore((s) => s.updateGlobal);
}

/**
 * Read-and-write hook for B2 (per-serial). Returns the per-device snapshot
 * (defaulting to the empty shape when no row exists yet) plus a setter
 * scoped to this serial.
 */
export function useDevicePrefs<T = DevicePrefs>(
  serial: string,
  selector?: (d: DevicePrefs) => T,
): T {
  return useMyPrefsStore((s) => {
    const d = s.devices[serial] ?? defaultDevice;
    return selector ? selector(d) : (d as unknown as T);
  });
}

export function useDevicePrefsUpdate(serial: string): (patch: DeepPartial<DevicePrefs>) => void {
  const update = useMyPrefsStore((s) => s.updateDevice);
  return (patch) => update(serial, patch);
}

/**
 * Per-(serial, package) sub-selector — reads `devices[serial].packages[pkg]`
 * (defaulting to `{}`) and yields a writer that scopes patches into that
 * subtree. Used by the Control page where every connected app package
 * carries its own pinned-command list / collapsed-section state.
 *
 * 注意：这里用一个稳定的 `EMPTY_PACKAGE` 常量作为缺省返回值；不要用
 * `?? {}` —— 那会让没初始化的 package 每次 render 都拿到新对象，触发
 * useSyncExternalStore 重渲循环（同 SaveStatusBar 白屏案例）。
 */
const EMPTY_PACKAGE: Readonly<Record<string, unknown>> = Object.freeze({});

export function useDevicePackagePrefs<T = Record<string, unknown>>(
  serial: string,
  pkg: string,
  selector?: (p: Record<string, unknown>) => T,
): T {
  return useMyPrefsStore((s) => {
    const d = s.devices[serial] ?? defaultDevice;
    const p = d.packages[pkg] ?? EMPTY_PACKAGE;
    return selector ? selector(p) : (p as unknown as T);
  });
}

export function useDevicePackagePrefsUpdate(
  serial: string,
  pkg: string,
): (patch: Record<string, unknown>) => void {
  const update = useMyPrefsStore((s) => s.updateDevice);
  return (patch) => update(serial, { packages: { [pkg]: patch } });
}

/** Status flags for the Settings page badge. */
export function useMyPrefsStatus(): { ready: boolean; saving: boolean; lastError: string | null } {
  // 每个字段独立订阅：返回一个新对象给调用方是 OK 的，因为它不会再被
  // 喂回 useSyncExternalStore；单字段 selector 走 Object.is 比较自然稳定。
  // 千万别写成 `useMyPrefsStore((s) => ({ ready, saving, lastError }))` —— 那个
  // 形状每次都是新对象，zustand v5 不浅比对，settings 页就是这一条 selector
  // 触发了 "getSnapshot should be cached" 死循环 → 整个子树 unmount → 白屏。
  const ready = useMyPrefsStore((s) => s.ready);
  const saving = useMyPrefsStore((s) => s.saving);
  const lastError = useMyPrefsStore((s) => s.lastError);
  return { ready, saving, lastError };
}

/* ----------------------------------------------------------------- */
/* Helpers                                                           */
/* ----------------------------------------------------------------- */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge<T>(base: T, patch: unknown): T {
  if (patch === undefined) return base;
  if (patch === null) return null as unknown as T;
  if (Array.isArray(patch)) return patch as unknown as T;
  if (!isPlainObject(patch)) return patch as T;
  if (!isPlainObject(base)) return patch as unknown as T;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    out[k] = deepMerge((base as Record<string, unknown>)[k], v);
  }
  return out as T;
}

/**
 * Stamp the response onto our default skeleton so callers can rely on
 * every key existing (avoids `s.global.ui.dashboard.collapsed[id]` crashes
 * on a fresh user who has no prefs yet).
 */
function deepMergeKnown<T>(skeleton: T, fromServer: unknown): T {
  if (!isPlainObject(fromServer)) return skeleton;
  return deepMerge(skeleton, fromServer);
}

function coerceDevices(raw: unknown): DevicesPrefs {
  if (!isPlainObject(raw)) return {};
  const out: DevicesPrefs = {};
  for (const [serial, val] of Object.entries(raw)) {
    if (!isPlainObject(val)) continue;
    out[serial] = deepMerge(defaultDevice, val);
  }
  return out;
}
